import {
  PRESET_GROUPS,
  PRESET_IDS,
  type ILegacyPremadeBlocks,
  type IPremadeBlocks,
  type IPremadeEntry,
  type IPremadePresetId,
} from "@/schemas/premadeBlock/premadeBlockSchema";

interface ResolveInput {
  /** Custom groups and preset markers, in the integrator's order. */
  entries: IPremadeEntry[];
  /** `true` drops unreferenced defaults, `false` appends them at the end. */
  override: boolean;
  /** The bundled default block sets for the current editor language. */
  defaults: ILegacyPremadeBlocks;
  /** Translate function, receives keys like "premade.header". */
  t: (key: string) => string;
}

/**
 * Turns the advanced `premadeBlocks.blocks` array into the flat list of groups
 * the sidebar renders. Pure: no store, no i18n import.
 */
export function resolvePremadeBlocks({
  entries,
  override,
  defaults,
  t,
}: ResolveInput): IPremadeBlocks {
  // Zero or one group, so a preset whose default set is absent drops out of
  // the flatMap without a null check at either call site.
  const defaultGroup = (preset: IPremadePresetId): IPremadeBlocks => {
    const { legacyKey, nameKey } = PRESET_GROUPS[preset];
    const blocks = defaults[legacyKey];
    return blocks ? [{ name: t(nameKey), blocks }] : [];
  };

  const placed = entries.flatMap((entry) =>
    "preset" in entry ? defaultGroup(entry.preset) : [entry]
  );

  if (override) return placed;

  const referenced = new Set(
    entries.flatMap((entry) => ("preset" in entry ? [entry.preset] : []))
  );

  return [
    ...placed,
    ...PRESET_IDS.filter((preset) => !referenced.has(preset)).flatMap(
      defaultGroup
    ),
  ];
}
